/**
 * Engraving cart guard.
 *
 * Two jobs, both on the mini cart (templates/cart.liquid only redirects to it, so it is the
 * only cart surface):
 *
 *  1. Remove any engraving line whose parent item has left the cart. The engraving fee is its
 *     own cart line, so without this a customer can delete the item being engraved and check
 *     out paying for an engraving with nothing to engrave.
 *
 *  2. Show the engraved message on the engraving line. The mini cart markup renders an empty
 *     .mcart-item__meta container per line and never fills it, so a customer could not otherwise
 *     check what they typed before paying for a personalised, made-to-order item.
 *
 * Loaded globally from snippets/zpro-minicart.liquid. Nothing in the theme's own files is
 * modified; the mini cart is re-rendered by AJAX, so filling the meta container here costs no
 * perceptible time compared with rendering it in Liquid.
 */
(function () {
  'use strict';

  var ENGRAVING_HANDLE = 'custom-engraving';
  var PARENT_PROPERTY = 'Item';
  var DECORATED_CLASS = 'mcart-item__engraving';
  var running = false;
  var cartCache = null;
  var cartCacheAt = 0;

  function isEngravingLine(item) {
    if (!item) return false;
    if (item.handle === ENGRAVING_HANDLE) return true;
    // Fallback if the product handle ever changes: a line carrying a parent reference.
    return !!(item.properties && item.properties[PARENT_PROPERTY] && item.properties.Engraving);
  }

  function cartUrl(path) {
    if (window.PROJ && typeof PROJ.cartUrl === 'function') return PROJ.cartUrl(path);
    var root =
      (window.Shopify && Shopify.routes && Shopify.routes.root) ||
      '/';
    if (root.slice(-1) !== '/') root += '/';
    return root + String(path || '').replace(/^\//, '');
  }

  function getCart(force) {
    var now = Date.now();
    if (!force && cartCache && now - cartCacheAt < 400) return Promise.resolve(cartCache);
    return fetch(cartUrl('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        cartCache = cart;
        cartCacheAt = Date.now();
        return cart;
      });
  }

  function removeLine(key) {
    return fetch(cartUrl('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
  }

  /* ---------------------------------------------------------------- orphan removal */

  function reconcile() {
    if (running) return Promise.resolve(false);
    running = true;

    return getCart(true)
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return false;

        var engravingLines = [];
        var parentTitles = [];

        cart.items.forEach(function (item) {
          if (isEngravingLine(item)) {
            engravingLines.push(item);
          } else {
            parentTitles.push(item.product_title);
          }
        });

        if (!engravingLines.length) return false;

        var orphans = engravingLines.filter(function (line) {
          var parent = line.properties && line.properties[PARENT_PROPERTY];
          // With no parent reference we cannot tell, so leave it rather than delete
          // something the customer may have meant to keep.
          if (!parent) return false;
          return parentTitles.indexOf(parent) === -1;
        });

        if (!orphans.length) return false;

        return orphans
          .reduce(function (chain, line) {
            return chain.then(function () {
              return removeLine(line.key);
            });
          }, Promise.resolve())
          .then(function () {
            return true;
          });
      })
      .catch(function (error) {
        if (window.console) console.error('[engraving-cart-guard] reconcile failed', error);
        return false;
      })
      .finally(function () {
        running = false;
      });
  }

  function reconcileAndRefresh() {
    return reconcile().then(function (removed) {
      if (!removed) {
        decorate();
        return;
      }
      // Removal used raw fetch, not cartRequest, so this cannot re-enter the wrapper below.
      if (window.PROJ && PROJ.ajaxcart && typeof PROJ.ajaxcart.updateCart === 'function') {
        PROJ.ajaxcart.updateCart();
      }
    });
  }

  /* ---------------------------------------------------------------- message display */

  function ensureStyles() {
    if (document.getElementById('engraving-meta-styles')) return;
    var style = document.createElement('style');
    style.id = 'engraving-meta-styles';
    style.textContent =
      '.' + DECORATED_CLASS + '{display:block;margin-top:4px;font-size:12px;line-height:1.45;' +
      'color:#555;}' +
      '.' + DECORATED_CLASS + ' span{display:block;overflow-wrap:anywhere;}' +
      '.' + DECORATED_CLASS + ' b{font-weight:600;color:#222;}';
    document.head.appendChild(style);
  }

  function visibleProperties(item) {
    var out = [];
    if (!item || !item.properties) return out;
    Object.keys(item.properties).forEach(function (key) {
      var value = item.properties[key];
      if (!value) return;
      if (key.charAt(0) === '_') return; // Shopify convention: hidden property
      out.push({ label: key, value: String(value) });
    });
    return out;
  }

  function fillMeta(row, item) {
    var props = visibleProperties(item);
    var existing = row.querySelector('.' + DECORATED_CLASS);

    if (!props.length) {
      if (existing) existing.remove();
      return;
    }

    var html = props
      .map(function (p) {
        var span = document.createElement('span');
        var b = document.createElement('b');
        b.textContent = p.label + ': ';
        span.appendChild(b);
        span.appendChild(document.createTextNode(p.value));
        return span.outerHTML;
      })
      .join('');

    var target = existing;
    if (!target) {
      target = document.createElement('div');
      target.className = DECORATED_CLASS;
      var meta = row.querySelector('.mcart-item__meta');
      if (meta) {
        meta.appendChild(target);
      } else {
        var details = row.querySelector('.details') || row;
        details.appendChild(target);
      }
    }

    if (target.innerHTML !== html) target.innerHTML = html;
  }

  /**
   * Match cart lines to mini cart rows. Rows expose only the variant id, and two engraving lines
   * can share one variant id with different messages, so lines are matched in document order
   * within each variant id rather than by id alone.
   */
  function decorate() {
    var root = document.getElementById('mini-cart');
    if (!root) return;

    var rows = [].slice.call(root.querySelectorAll('.mcart-item[data-proid]'));
    if (!rows.length) return;

    ensureStyles();

    getCart(false)
      .then(function (cart) {
        if (!cart || !cart.items) return;

        var buckets = {};
        cart.items.forEach(function (item) {
          var id = String(item.variant_id);
          if (!buckets[id]) buckets[id] = [];
          buckets[id].push(item);
        });

        var seen = {};
        rows.forEach(function (row) {
          var id = row.getAttribute('data-proid');
          if (!seen[id]) seen[id] = 0;
          var item = (buckets[id] || [])[seen[id]];
          seen[id] += 1;
          if (item) fillMeta(row, item);
        });
      })
      .catch(function (error) {
        if (window.console) console.error('[engraving-cart-guard] decorate failed', error);
      });
  }

  /** The mini cart is replaced wholesale on every AJAX refresh, so re-decorate on change. */
  function watchMiniCart() {
    var root = document.getElementById('mini-cart');
    if (!root || !window.MutationObserver) return;

    var scheduled = false;
    var observer = new MutationObserver(function (mutations) {
      // Ignore our own insertions.
      var relevant = mutations.some(function (m) {
        return [].slice.call(m.addedNodes).some(function (n) {
          return !(n.classList && n.classList.contains(DECORATED_CLASS));
        });
      });
      if (!relevant || scheduled) return;
      scheduled = true;
      setTimeout(function () {
        scheduled = false;
        decorate();
      }, 60);
    });

    observer.observe(root, { childList: true, subtree: true });
  }

  /* ---------------------------------------------------------------- theme hooks */

  /** Run after every cart mutation the theme performs. */
  function patchCartRequest() {
    if (!window.PROJ || !PROJ.ajaxcart || typeof PROJ.ajaxcart.cartRequest !== 'function') {
      return false;
    }
    if (PROJ.ajaxcart.cartRequest.__engravingGuarded) return true;

    var original = PROJ.ajaxcart.cartRequest;

    var wrapped = function () {
      var result = original.apply(this, arguments);
      return Promise.resolve(result).then(function (value) {
        return reconcileAndRefresh().then(function () {
          return value;
        });
      });
    };

    wrapped.__engravingGuarded = true;
    PROJ.ajaxcart.cartRequest = wrapped;
    return true;
  }

  function waitForTheme() {
    if (patchCartRequest()) return;
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (patchCartRequest() || attempts > 100) clearInterval(timer);
    }, 100);
  }

  function init() {
    waitForTheme();
    watchMiniCart();
    reconcileAndRefresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
